import React from 'react';
import { useParams } from "react-router-dom";
import { Spinner } from "reactstrap";

const ICONS = {
  critical: "ri-error-warning-fill",
  high:     "ri-alert-fill",
  medium:   "ri-information-fill",
  low:      "ri-checkbox-circle-fill",
  ok:       "ri-shield-check-fill",
};

const SEV_STYLE = {
  critical: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  high:     { color: "#ea580c", bg: "#fff7ed", border: "#fed7aa" },
  medium:   { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  low:      { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  ok:       { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
};

function RecommendationSummary({ reportData, loading }) {
  const { agent } = useParams();

  const recommendations = [];
  const summaries       = [];

  if (!loading && reportData) {
    if (agent?.toLowerCase() === "all") {
      const allSummaries = Array.isArray(reportData.summary)
        ? reportData.summary
        : [reportData.summary].filter(Boolean);

      allSummaries.forEach(item => {
        const { critical_count = 0, high_count = 0, medium_count = 0, low_count = 0 } = item || {};
        const label = "Across all agents";
        if (critical_count > 0) {
          summaries.push({ text: `${label}, ${critical_count} critical threats detected.`, sev: "critical" });
          recommendations.push({ text: "Immediately patch systems to address critical threats across all agents.", sev: "critical" });
        }
        if (high_count > 0) {
          summaries.push({ text: `${label}, ${high_count} high severity threats present.`, sev: "high" });
          recommendations.push({ text: "Apply urgent fixes and review configurations to mitigate high severity threats.", sev: "high" });
        }
        if (medium_count > 0) {
          summaries.push({ text: `${label}, ${medium_count} medium severity threats identified.`, sev: "medium" });
          recommendations.push({ text: "Schedule remediation and increase monitoring for medium severity threats.", sev: "medium" });
        }
        if (low_count > 0) {
          summaries.push({ text: `${label}, ${low_count} low severity threats found.`, sev: "low" });
          recommendations.push({ text: "Include these in the routine patch cycle and compliance reporting.", sev: "low" });
        }
        if (critical_count + high_count + medium_count + low_count === 0) {
          summaries.push({ text: `${label}, no active threats detected.`, sev: "ok" });
          recommendations.push({ text: "Maintain monitoring and routine patching across all agents.", sev: "ok" });
        }
      });
    } else {
      const summary = reportData.summary || {};
      const { critical_count = 0, high_count = 0, medium_count = 0, low_count = 0 } = summary;

      if (critical_count > 0) {
        recommendations.push({ text: `Patch ${agent} immediately due to active critical threats.`, sev: "critical" });
        recommendations.push({ text: `Isolate ${agent} from external networks until remediation is complete.`, sev: "critical" });
        recommendations.push({ text: "Enable continuous monitoring for exploitation attempts.", sev: "critical" });
        summaries.push({ text: `${agent} is highest priority with ${critical_count} critical threats.`, sev: "critical" });
      }
      if (high_count > 0) {
        recommendations.push({ text: `Apply urgent fixes on ${agent} for high severity issues.`, sev: "high" });
        recommendations.push({ text: "Review firewall and access control configurations.", sev: "high" });
        recommendations.push({ text: "Update application frameworks to latest versions.", sev: "high" });
        summaries.push({ text: `${agent} has ${high_count} high severity threats — urgent remediation needed.`, sev: "high" });
      }
      if (medium_count > 0) {
        recommendations.push({ text: `Schedule remediation for medium severity threats on ${agent}.`, sev: "medium" });
        recommendations.push({ text: "Conduct weekly vulnerability scans.", sev: "medium" });
        recommendations.push({ text: "Ensure logging and alerting is enabled for suspicious activity.", sev: "medium" });
        summaries.push({ text: `${agent} has ${medium_count} medium severity threats — monitor and schedule fixes.`, sev: "medium" });
      }
      if (low_count > 0) {
        recommendations.push({ text: `Include ${agent} in routine patch cycle for low severity threats.`, sev: "low" });
        recommendations.push({ text: "Document findings for compliance reporting.", sev: "low" });
        recommendations.push({ text: "Educate team on secure configuration practices.", sev: "low" });
        summaries.push({ text: `${agent} has ${low_count} low severity threats — address in routine patch cycle.`, sev: "low" });
      }
      if (critical_count + high_count + medium_count + low_count === 0) {
        summaries.push({ text: `${agent} has no active threats detected.`, sev: "ok" });
        recommendations.push({ text: `Maintain monitoring and routine patching for ${agent}.`, sev: "ok" });
      }
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af", fontSize: 13 }}>
        <Spinner size="sm" style={{ marginRight: 8, color: "#FF5B00" }} />
        Loading recommendations...
      </div>
    );
  }

  const SectionHeader = ({ title }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <div style={{ width: 4, height: 20, background: "#FF5B00", borderRadius: 2, flexShrink: 0 }} />
      <h6 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1f2937", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {title}
      </h6>
    </div>
  );

  const RecItem = ({ item }) => {
    const s = SEV_STYLE[item.sev] || SEV_STYLE.ok;
    const icon = ICONS[item.sev] || ICONS.ok;
    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, marginBottom: 8 }}>
        <i className={icon} style={{ color: s.color, fontSize: 16, flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.55 }}>{item.text}</span>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <SectionHeader title={agent?.toLowerCase() === "all" ? "Recommendations — All Agents" : `Recommendations — ${agent}`} />
        {recommendations.slice(0, 3).map((rec, i) => <RecItem key={i} item={rec} />)}
      </div>

      <div style={{ marginBottom: 28 }}>
        <SectionHeader title="Summary" />
        {summaries.slice(0, 2).map((s, i) => <RecItem key={i} item={s} />)}
      </div>
    </div>
  );
}

export default RecommendationSummary;
